/**
 * Phase 19 — admin "Customer-described dimensions" panel.
 *
 * Reads the estimate's `known_dimensions` anchors (Stella derived
 * these from the customer's chat description) and surfaces them as
 * one-glance bullets above the existing `DimensionsPanel`. Lets the
 * admin reviewing an estimate see "the wall I'm pricing is 14 ft ×
 * 10 ft = 140 sqft" without having to scroll the chat history.
 *
 * Component is hidden (returns null) when no `known_dimensions`
 * anchors exist on the estimate — no empty card cluttering the
 * workspace column.
 */

import { useEffect, useState } from "react";

import { listAnchors } from "@starz/shared/api/sdk.gen";
import type { ScaleAnchor } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
}

interface ParsedDim {
  widthIn: number;
  heightIn: number | null;
}

/**
 * Mirror of the Python `_parse_width_height` from
 * `api/domain/anchor_dimensions.py` (Phase 19). Returns the parsed
 * width in inches plus the height in inches when both can be
 * extracted; height stays `null` for width-only strings ("14 feet
 * wide closet, height TBD"). Never invents a height.
 */
function parseWidthHeight(valueText: string): ParsedDim | null {
  if (!valueText) return null;
  const pattern =
    /(\d+(?:\.\d+)?)\s*(ft|feet|foot|inches?|in|'|")\s*(?:wide)?\s*(?:by|x|×|,)\s*(\d+(?:\.\d+)?)\s*(ft|feet|foot|inches?|in|'|")\s*(?:tall|high)?/i;
  const match = valueText.match(pattern);
  if (!match) {
    // Try a width-only fallback for the "14 feet wide closet" shape.
    const widthOnly =
      /(\d+(?:\.\d+)?)\s*(ft|feet|foot|inches?|in|'|")\s*(?:wide)?/i.exec(
        valueText,
      );
    if (!widthOnly) return null;
    const w = Number(widthOnly[1]) * unitToInches(widthOnly[2]);
    if (!Number.isFinite(w) || w <= 0) return null;
    return { widthIn: w, heightIn: null };
  }
  const w = Number(match[1]) * unitToInches(match[2]);
  const h = Number(match[3]) * unitToInches(match[4]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  return { widthIn: w, heightIn: h };
}

function unitToInches(unit: string): number {
  const u = unit.toLowerCase().replace(/s$/, "");
  if (u === "ft" || u === "feet" || u === "foot" || u === "'") return 12;
  return 1; // inches / in / "
}

function inToFt(inches: number): number {
  return inches / 12;
}

function formatBullet(anchor: ScaleAnchor): string {
  const text = anchor.value_text ?? "";
  const parsed = parseWidthHeight(text);
  if (parsed === null) {
    // Anchor doesn't parse — fall back to showing the raw text so the
    // admin still sees what the customer described.
    return text || "(unparseable dimension)";
  }
  const wFt = inToFt(parsed.widthIn);
  if (parsed.heightIn === null) {
    return `${formatFt(wFt)} wide`;
  }
  const hFt = inToFt(parsed.heightIn);
  const areaSqft = (parsed.widthIn * parsed.heightIn) / 144;
  return `${formatFt(wFt)} × ${formatFt(hFt)} = ${formatSqft(areaSqft)}`;
}

function formatFt(value: number): string {
  // Strip trailing .0 for clean output: "14 ft" not "14.0 ft".
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${text} ft`;
}

function formatSqft(value: number): string {
  const rounded = Math.round(value);
  return `${rounded} sqft`;
}

export function ProjectDimensionsPanel({ estimateId }: Props) {
  const [anchors, setAnchors] = useState<ScaleAnchor[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await listAnchors({ estimateId });
      if (cancelled || "error" in res) return;
      setAnchors(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  if (anchors === null) return null;

  const knownDims = anchors
    .filter((a) => a.kind === "known_dimensions")
    .sort((a, b) => (a.created_at < b.created_at ? -1 : 1));

  // Hide entirely when the estimate has no customer-described
  // dimensions — no empty card cluttering the workspace column.
  if (knownDims.length === 0) return null;

  return (
    <section className="rounded-xl border border-starz-gold/20 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Customer-described dimensions
      </h3>
      <ul className="space-y-1.5">
        {knownDims.map((a) => (
          <li key={a.anchor_id} className="text-sm text-starz-charcoal">
            <span className="font-medium">{formatBullet(a)}</span>
            {a.value_text && (
              <span className="ml-2 text-xs italic text-warm-gray">
                — "{a.value_text}"
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] italic text-warm-gray">
        Captured from the customer's chat description. Pricing prefers
        these values over the AI's whole-room estimate.
      </p>
    </section>
  );
}
